import { Input } from '@mui/base/Input';
import cx from 'classnames';
import type { ChangeEvent, KeyboardEvent } from 'react';
import type {
  Control,
  FieldPath,
  FieldValues,
  PathValue,
  UseControllerProps,
} from 'react-hook-form';
import { useController } from 'react-hook-form';
import typography from '~styles/typography.scss';
import styles from './ControlledFormCurrencyInput.scss';
import { FormTooltip } from './FormTooltip';
import { LayoutVariant } from './sharedTypes';
import { ValidationErrorMessage } from './ValidationErrorMessage';

export interface ControlledFormCurrencyInputProps<
  T extends FieldValues = FieldValues,
  Name extends FieldPath<T> = FieldPath<T>,
> {
  className?: string;
  control: Control<T>;
  defaultValue?: PathValue<T, Name>;
  disabled?: boolean;
  label: string;
  name: Name;
  placeholder?: string;
  rules?: UseControllerProps<T, Name>['rules'];
  tooltipText?: string;
  variant?: LayoutVariant;
  labelClassName?: string;
  darkMode?: boolean;
  /** Whole dollars only — no cents; values are rounded to an integer. */
  integerDollarsOnly?: boolean;
}

export const ControlledFormCurrencyInput = <
  T extends FieldValues = FieldValues,
  Name extends FieldPath<T> = FieldPath<T>,
>({
  className,
  control,
  defaultValue,
  disabled,
  label,
  name,
  placeholder,
  rules = {},
  tooltipText,
  variant = LayoutVariant.Vertical,
  labelClassName = '',
  darkMode = false,
  integerDollarsOnly = false,
}: ControlledFormCurrencyInputProps<T, Name>) => {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    control,
    defaultValue,
    name,
    rules,
  });

  const handleIntegerChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      onChange('');
      return;
    }
    const n = Number.parseFloat(raw);
    if (Number.isNaN(n)) {
      onChange('');
      return;
    }
    onChange(String(Math.round(n)));
  };

  const handleKeyDownInteger = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === '.' || e.key === ',') {
      e.preventDefault();
    }
  };

  const displayValue = (() => {
    if (value === undefined || value === null || value === '') {
      return '';
    }
    if (!integerDollarsOnly) {
      return String(value);
    }
    const n = Number(value);
    if (Number.isNaN(n)) {
      return String(value);
    }
    return String(Math.round(n));
  })();

  const showCurrency = value !== undefined && value !== null && value !== '';
  const currencyAdornment = (
    <span
      className={styles.currency}
      style={{ visibility: showCurrency ? undefined : 'hidden' }}
    >
      $
    </span>
  );

  return (
    <div
      className={cx(styles.fieldContainer, styles[variant], className, {
        [styles.darkMode]: darkMode,
      })}
    >
      <div className={styles.labelContainer}>
        <label
          className={cx({
            [typography.c2_20]: true,
            [styles.labelError]: !!error,
            [labelClassName]: !!labelClassName,
          })}
          htmlFor={name}
        >
          {label}
        </label>
        <FormTooltip label={name} text={tooltipText} />
      </div>
      <div className={styles.inputContainer}>
        <Input
          autoComplete="off"
          disabled={disabled}
          id={name}
          onChange={integerDollarsOnly ? handleIntegerChange : onChange}
          onKeyDown={integerDollarsOnly ? handleKeyDownInteger : undefined}
          onWheel={() => (document.activeElement as HTMLInputElement).blur()}
          placeholder={placeholder}
          slotProps={{
            root: {
              className: cx(typography.t1, styles.inputRoot, {
                [styles.inputError]: !!error,
              }),
            },
            input: {
              'aria-errormessage': `${name}-error`,
              'aria-invalid': !!error,
              ...(integerDollarsOnly && { step: 1 }),
            },
          }}
          startAdornment={currencyAdornment}
          type="number"
          value={displayValue}
        />
        <ValidationErrorMessage error={error} label={label} name={name} />
      </div>
    </div>
  );
};
